import { useState, useEffect, type ReactNode } from 'react';
import { File as FileEdit, Download, Calendar, User, Building2, Loader as Loader2, Sparkles, CircleCheck as CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { useApi } from '../../lib/useApi';
import type { Account } from '../../types';
import type { EAWorkspace, EAAnalyticsSnapshot, EAReportDraft, ReportStyle, CurrencyCode } from '../../types/executiveAnalytics';
import FormattedText from '../common/FormattedText';

interface ReportTabProps {
  account: Account;
  workspace: EAWorkspace;
  snapshot: EAAnalyticsSnapshot | null;
  onWorkspaceUpdate: () => Promise<void>;
}

const STYLE_OPTIONS: { value: ReportStyle; label: string }[] = [
  { value: 'executive_brief', label: 'Executive Brief' },
  { value: 'cfo_focused', label: 'CFO-Focused' },
  { value: 'cfo_cto_joint', label: 'CFO-CTO Joint' },
  { value: 'board_summary', label: 'Board Summary' },
  { value: 'concise', label: 'Concise' },
  { value: 'full_detailed', label: 'Full Detailed' },
];

const RECOMMENDATION_LABELS: Record<string, string> = {
  strong_proceed: 'Strong Proceed',
  proceed_with_conditions: 'Proceed with Conditions',
  needs_more_validation: 'Needs Validation',
  weak_case: 'Weak Case',
  do_not_present: 'Do Not Present',
};

const SECTION_LABELS: Record<string, string> = {
  executive_summary: 'Executive Summary',
  financial_analysis: 'Financial Analysis',
  scenario_analysis: 'Scenario Analysis',
  strategic_alignment: 'Strategic Alignment',
  risk_assessment: 'Risk Assessment',
  recommendation_narrative: 'Recommendation',
  next_steps: 'Next Steps',
};

export default function ReportTab({ account, workspace, snapshot, onWorkspaceUpdate }: ReportTabProps) {
  const { apiGet, apiPut, apiPost } = useApi();
  const [report, setReport] = useState<Partial<EAReportDraft>>({
    workspace_id: workspace.id,
    executive_first_name: '',
    executive_last_name: '',
    executive_title: '',
    company_name: account.company_name,
    subtitle: '',
    cover_note: '',
    author_name: '',
    report_date: new Date().toISOString().split('T')[0],
    report_style: 'executive_brief',
    generated_sections: {} as Record<string, string>,
  });
  const [reportId, setReportId] = useState<string | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [generatingNarrative, setGeneratingNarrative] = useState(false);
  const [narrativeError, setNarrativeError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const sections = report.generated_sections as Record<string, string> | undefined;
  const hasNarratives = sections && Object.keys(sections).length > 0;

  useEffect(() => {
    loadReport();
  }, [workspace.id]);

  const buildReportPayload = (r: Partial<EAReportDraft>) => ({
    executive_first_name: r.executive_first_name ?? '',
    executive_last_name: r.executive_last_name ?? '',
    executive_title: r.executive_title ?? '',
    company_name: r.company_name ?? '',
    subtitle: r.subtitle ?? '',
    cover_note: r.cover_note ?? '',
    author_name: r.author_name ?? '',
    report_date: r.report_date ?? '',
    report_style: r.report_style ?? 'executive_brief',
    snapshot_id: snapshot?.id ?? null,
  });

  const loadReport = async () => {
    const { report: draft } = await apiGet<{ report: EAReportDraft | null }>(
      `/v1/ea/workspace/${workspace.id}/report`,
    );
    if (draft) {
      setReport(draft);
      setReportId(draft.id);
    }
    setLoaded(true);
  };

  const ensureReportId = async (): Promise<string | null> => {
    if (reportId) return reportId;
    const { report: saved } = await apiPut<{ report: EAReportDraft }>(
      `/v1/ea/workspace/${workspace.id}/report`,
      buildReportPayload(report),
    );
    setReportId(saved.id);
    return saved.id;
  };

  const saveField = async (field: string, value: string) => {
    const updated = { ...report, [field]: value };
    setReport(updated);

    const { report: saved } = await apiPut<{ report: EAReportDraft }>(
      `/v1/ea/workspace/${workspace.id}/report`,
      buildReportPayload(updated),
    );
    if (!reportId) setReportId(saved.id);
  };

  const handleGenerateNarrative = async () => {
    if (!snapshot) return;
    setGeneratingNarrative(true);
    setNarrativeError(null);
    try {
      const rid = await ensureReportId();
      await apiPost('/v1/ea/generate-report', { workspace_id: workspace.id, report_id: rid });
      await loadReport();
      await onWorkspaceUpdate();
    } catch (err: unknown) {
      setNarrativeError(err instanceof Error ? err.message : 'Failed to generate narratives');
    } finally {
      setGeneratingNarrative(false);
    }
  };

  const handleGeneratePDF = async () => {
    if (!snapshot) return;
    setGeneratingPdf(true);
    try {
      const { generatePDF } = await import('../../lib/executive-analytics/pdfRenderer');
      const blob = await generatePDF(
        snapshot,
        report as EAReportDraft,
        account.company_name,
        account.industry || '',
        (workspace.currency || 'USD') as CurrencyCode,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `EA_Report_${account.company_name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setGeneratingPdf(false);
    }
  };

  if (!loaded) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-app-brand animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-app-surface rounded-lg border border-app-line p-6">
        <div className="flex items-center gap-2 mb-6">
          <FileEdit className="w-5 h-5 text-app-brand" />
          <h3 className="text-lg font-semibold text-app-ink">Report Configuration</h3>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField icon={<User className="w-4 h-4" />} label="Executive First Name" value={report.executive_first_name || ''} onChange={v => saveField('executive_first_name', v)} />
          <FormField label="Executive Last Name" value={report.executive_last_name || ''} onChange={v => saveField('executive_last_name', v)} />
          <FormField label="Executive Title" value={report.executive_title || ''} onChange={v => saveField('executive_title', v)} />
          <FormField icon={<Building2 className="w-4 h-4" />} label="Company Name" value={report.company_name || account.company_name} onChange={v => saveField('company_name', v)} />
          <FormField label="Subtitle (Optional)" value={report.subtitle || ''} onChange={v => saveField('subtitle', v)} />
          <FormField label="Author Name" value={report.author_name || ''} onChange={v => saveField('author_name', v)} />
          <div>
            <label className="block text-sm font-medium text-app-ink mb-1">
              <span className="inline-flex items-center gap-1"><Calendar className="w-4 h-4" /> Report Date</span>
            </label>
            <input
              type="date"
              value={report.report_date || ''}
              onChange={e => { setReport(prev => ({ ...prev, report_date: e.target.value })); }}
              onBlur={e => saveField('report_date', e.target.value)}
              className="w-full px-3 py-2 border border-app-line-strong rounded-lg focus:ring-2 focus:ring-app-focus focus:border-transparent text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-app-ink mb-1">Report Style</label>
            <select
              value={report.report_style || 'executive_brief'}
              onChange={e => { const v = e.target.value as ReportStyle; setReport(prev => ({ ...prev, report_style: v })); saveField('report_style', v); }}
              className="w-full px-3 py-2 border border-app-line-strong rounded-lg focus:ring-2 focus:ring-app-focus focus:border-transparent text-sm"
            >
              {STYLE_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-app-ink mb-1">Cover Note (Optional)</label>
            <textarea
              value={report.cover_note || ''}
              onChange={e => setReport(prev => ({ ...prev, cover_note: e.target.value }))}
              onBlur={e => saveField('cover_note', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-app-line-strong rounded-lg focus:ring-2 focus:ring-app-focus focus:border-transparent text-sm"
            />
          </div>
        </div>
      </div>

      {snapshot && (
        <div className="bg-app-positive/10 rounded-lg border border-app-brand/20 p-6">
          <h3 className="text-lg font-semibold text-app-ink mb-4">Preview Summary</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <div className="text-sm text-app-muted mb-1">Recommendation</div>
              <div className="inline-block px-3 py-1 bg-app-surface rounded-full text-sm font-medium text-app-brand border border-app-brand/20">
                {RECOMMENDATION_LABELS[snapshot.recommendation] || snapshot.recommendation || 'Pending'}
              </div>
            </div>
            <div>
              <div className="text-sm text-app-muted mb-1">Annualised ROI</div>
              <div className="text-2xl font-bold text-app-ink">{(snapshot.financial_outputs.annualised_roi_percent ?? snapshot.financial_outputs.roi_percent).toFixed(0)}%</div>
            </div>
            <div>
              <div className="text-sm text-app-muted mb-1">Payback Period</div>
              <div className="text-2xl font-bold text-app-ink">{snapshot.financial_outputs.payback_months ?? 'N/A'} mo</div>
            </div>
            <div>
              <div className="text-sm text-app-muted mb-1">Deal Quality</div>
              <div className="text-2xl font-bold text-app-ink">{snapshot.scores?.deal_quality?.toFixed(0) ?? 'N/A'} / 100</div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-app-surface rounded-lg border border-app-line p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-app-brand" />
            <h3 className="text-lg font-semibold text-app-ink">Executive narrative</h3>
          </div>
          {hasNarratives && (
            <span className="flex items-center gap-1 text-sm text-app-positive bg-app-positive/10 px-2.5 py-1 rounded-full">
              <CheckCircle className="w-3.5 h-3.5" /> Generated
            </span>
          )}
        </div>

        {hasNarratives ? (
          <div className="space-y-2 mb-4">
            {Object.entries(sections!).map(([key, content]) => (
              <div key={key} className="border border-app-line rounded-lg">
                <button
                  onClick={() => setExpandedSection(expandedSection === key ? null : key)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-app-canvas transition-colors"
                >
                  <span className="text-sm font-medium text-app-ink">{SECTION_LABELS[key] || key.replace(/_/g, ' ')}</span>
                  {expandedSection === key ? <ChevronUp className="w-4 h-4 text-app-muted" /> : <ChevronDown className="w-4 h-4 text-app-muted" />}
                </button>
                {expandedSection === key && (
                  <div className="px-4 pb-4 border-t border-app-line pt-3">
                    <FormattedText text={content} className="text-sm text-app-ink" />
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-app-muted mb-4">
            Generate evidence-led narrative sections for the report. They will sit alongside the financial data and charts in the final PDF.
          </p>
        )}

        {narrativeError && <p className="text-sm text-app-critical mb-3">{narrativeError}</p>}

        <button
          onClick={handleGenerateNarrative}
          disabled={!snapshot || generatingNarrative}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-app-brand/10 border border-app-brand/20 text-app-brand rounded-lg font-medium hover:bg-app-brand/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {generatingNarrative ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Generating narrative...</>
          ) : hasNarratives ? (
            <><Sparkles className="w-4 h-4" /> Regenerate narrative</>
          ) : (
            <><Sparkles className="w-4 h-4" /> Generate narrative</>
          )}
        </button>
      </div>

      <button
        onClick={handleGeneratePDF}
        disabled={!snapshot || generatingPdf}
        className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-app-brand hover:bg-app-brand disabled:bg-app-line-strong disabled:cursor-not-allowed text-white rounded-lg font-medium text-lg transition-colors"
      >
        {generatingPdf ? (
          <><Loader2 className="w-5 h-5 animate-spin" /> Generating PDF...</>
        ) : (
          <><Download className="w-5 h-5" /> Generate PDF Report</>
        )}
      </button>
    </div>
  );
}

function FormField({ label, value, onChange, icon }: { label: string; value: string; onChange: (v: string) => void; icon?: ReactNode }) {
  const [local, setLocal] = useState(value);
  useEffect(() => { setLocal(value); }, [value]);
  return (
    <div>
      <label className="block text-sm font-medium text-app-ink mb-1">
        {icon ? <span className="inline-flex items-center gap-1">{icon} {label}</span> : label}
      </label>
      <input
        type="text"
        value={local}
        onChange={e => setLocal(e.target.value)}
        onBlur={() => onChange(local)}
        className="w-full px-3 py-2 border border-app-line-strong rounded-lg focus:ring-2 focus:ring-app-focus focus:border-transparent text-sm"
      />
    </div>
  );
}
